import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";
import useOverlayDismiss from "../../Elements/useOverlayDismiss";
import apiFetch from "../../../context/api";
import { formatActivityDetails } from "./activityDetails";
import { cantidadDespachada, mermaDeLinea } from "../../../lib/helpers/products";
import { describirUbicacionMovimiento } from "../../../lib/helpers/locations";

/**
 * Drill-down de una fila de Actividad, al estilo del modal de Contabilidad: baja al
 * registro que hay DEBAJO del hecho registrado, en vez de reimprimir el mismo hecho.
 *
 * El log ya guarda `entity_type` + `entity_id`; eso es lo que permite bajar:
 *   - transaction (una venta)  → sus LÍNEAS: qué se vendió, a quién y a cuánto.
 *   - product (movimientos de stock) → los últimos movimientos de ese producto.
 *   - user (altas / ediciones) → no hay nivel inferior: solo los pares del propio log.
 */

const money = (n) => Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
const qty = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 3 });

const PAYMENT_METHODS = { cash: "Efectivo", card: "Tarjeta", credit: "Crédito", transfer: "Transferencia" };

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/** Cabecera común: qué pasó, quién y cuándo. */
function Meta({ item }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-slate-400">Cuándo</dt>
      <dd className="text-slate-700">{fmtWhen(item.created_at)}</dd>
      <dt className="text-slate-400">Quién</dt>
      <dd className="text-slate-700">
        {item.actor_email || "—"}
        {item.actor_role ? <span className="text-slate-400"> · {item.actor_role}</span> : null}
      </dd>
    </dl>
  );
}

/** Detalle de una VENTA: sus líneas y el cuadre de importes. */
function SaleDetail({ sale }) {
  return (
    <>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-slate-400">Socio</dt>
        <dd className="text-slate-700">
          {sale.member_name || "—"}
          {sale.member_number ? <span className="text-slate-400"> · nº {sale.member_number}</span> : null}
        </dd>
        <dt className="text-slate-400">Cobro</dt>
        <dd className="text-slate-700">{PAYMENT_METHODS[sale.payment_method] || sale.payment_method || "—"}</dd>
        {sale.processed_by && (
          <>
            <dt className="text-slate-400">Atendió</dt>
            <dd className="text-slate-700">{sale.processed_by}</dd>
          </>
        )}
      </dl>

      <table className="mt-4 w-full text-sm">
        {/* Va a sangre dentro del modal: `px-0` a propósito, para que la primera
            columna quede alineada con el texto de arriba y no sangrada 16px. */}
        <TableHead>
          <Th className="px-0 py-2">Producto</Th>
          <Th align="right" className="px-0 py-2">Cantidad</Th>
          <Th align="right" className="px-0 py-2">Precio</Th>
          <Th align="right" className="px-0 py-2">Importe</Th>
        </TableHead>
        <tbody>
          {sale.lines.length === 0 && (
            <tr><Td align="center" colSpan={4} className="py-6 text-[var(--muted-foreground)]">Esta venta no tiene líneas.</Td></tr>
          )}
          {sale.lines.map((l, i) => (
            <Tr key={i}>
              <Td className="py-2 pl-0 pr-2 font-medium text-[var(--foreground)]">{l.product_name}</Td>
              <Td align="right" className="px-0 py-2">
                {/* Lo DESPACHADO (báscula si la hubo); lo pedido y la merma, de nota. */}
                {qty(cantidadDespachada(l))}
                {mermaDeLinea(l) != null && mermaDeLinea(l) !== 0 && (
                  <div className="text-[11px] text-[var(--muted-foreground)]">
                    pedido: {qty(l.quantity)} · merma {mermaDeLinea(l) > 0 ? '+' : ''}{qty(mermaDeLinea(l))}
                  </div>
                )}
              </Td>
              <Td align="right" className="px-0 py-2">{money(l.unit_price)}</Td>
              <Td align="right" className="px-0 py-2">{money(l.line_subtotal)}</Td>
            </Tr>
          ))}
        </tbody>
      </table>

      {/* El precio de línea es el ORIGINAL: el descuento se aplicó al total de la venta
          y nunca se guardó línea a línea. Por eso se resta aquí y no ahí arriba. */}
      <dl className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-slate-500">Subtotal</dt>
          <dd className="text-slate-700">{money(sale.subtotal)}</dd>
        </div>
        {Number(sale.discount) > 0 && (
          <div className="flex justify-between">
            <dt className="text-slate-500">Descuento</dt>
            <dd className="text-emerald-700">−{money(sale.discount)}</dd>
          </div>
        )}
        <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold">
          <dt className="text-slate-800">Total cobrado</dt>
          <dd className="text-slate-900">{money(sale.total)}</dd>
        </div>
      </dl>
    </>
  );
}

/** Últimos movimientos del producto afectado. */
function ProductHistory({ history }) {
  return (
    <table className="w-full text-sm">
      <TableHead>
        <Th className="px-0 py-2">Movimiento</Th>
        <Th align="right" className="px-0 py-2">Cantidad</Th>
        <Th align="right" className="px-0 py-2">Saldo</Th>
        <Th align="right" className="px-0 py-2">Fecha</Th>
      </TableHead>
      <tbody>
        {history.movements.length === 0 && (
          <tr><Td align="center" colSpan={4} className="py-6 text-[var(--muted-foreground)]">Sin movimientos.</Td></tr>
        )}
        {history.movements.map((m, i) => (
          <Tr key={i}>
            <Td className="py-2 pl-0 pr-2">
              <span className="font-medium text-[var(--foreground)]">{m.movement_type}</span>
              {m.concept && <div className="text-[11px] text-[var(--muted-foreground)]">{m.concept}</div>}
              {describirUbicacionMovimiento(m) && (
                <div className="text-[11px] text-[var(--muted-foreground)]">{describirUbicacionMovimiento(m)}</div>
              )}
            </Td>
            {/* Entra o sale stock: signo, no categoría. */}
            <Td align="right" className={`px-0 py-2 font-medium ${Number(m.quantity) < 0 ? "text-[var(--destructive)]" : "text-[var(--success)]"}`}>
              {Number(m.quantity) >= 0 ? "+" : "−"}{qty(Math.abs(Number(m.quantity)))}
            </Td>
            <Td align="right" className="px-0 py-2 text-[var(--muted-foreground)]">{m.balance_after != null ? qty(m.balance_after) : "—"}</Td>
            <Td align="right" className="px-0 py-2 text-[var(--muted-foreground)]">{fmtWhen(m.created_at)}</Td>
          </Tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ActivityDetailModal({ item, token, venueID, locations, onClose }) {
  const overlayDismiss = useOverlayDismiss(onClose);
  const [sale, setSale] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  // El drill-down puede no estar disponible (endpoint aún no desplegado, o un STAFF sin
  // permiso de productos: /v1/stock/history exige `product_editor_privileges`). No es un
  // error que deba asustar a nadie — el modal cae al detalle del propio log.
  const [drillDownUnavailable, setDrillDownUnavailable] = useState(false);
  // Y esto es lo contrario: algo se rompió (404, 500, red, endpoint sin desplegar). No es
  // cosa de los permisos del usuario, y decírselo lo manda a buscar donde no está.
  const [drillDownError, setDrillDownError] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Cerrar con Escape: el modal se abre desde una fila de tabla y hasta ahora solo se podía
  // cerrar con el ratón.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const pairs = useMemo(
    () => formatActivityDetails(item.action, item.details, locations, item.summary),
    [item.action, item.details, locations, item.summary]
  );

  const load = useCallback(async () => {
    if (!item.entity_id) return;
    setLoading(true);
    setDrillDownUnavailable(false);
    setDrillDownError(false);
    try {
      if (item.entity_type === "transaction") {
        const res = await apiFetch(`/v1/transactions/${item.entity_id}`, { headers: authHeaders });
        if (!res.ok) throw new Error(String(res.status));
        setSale(await res.json());
      } else if (item.entity_type === "product") {
        const res = await apiFetch(
          `/v1/stock/history?product_id=${item.entity_id}&venue_id=${venueID}&limit=10`,
          { headers: authHeaders }
        );
        if (!res.ok) throw new Error(String(res.status));
        setHistory(await res.json());
      }
    } catch (err) {
      // Un 403 SÍ es "tu usuario no puede". Cualquier otra cosa (404, 500, red caída, el
      // endpoint aún sin desplegar) no lo es: decírselo a un OWNER es mentirle sobre sus
      // permisos y mandarlo a buscar el problema donde no está.
      if (String(err.message) === "403") setDrillDownUnavailable(true);
      else setDrillDownError(true);
    } finally {
      setLoading(false);
    }
  }, [item.entity_id, item.entity_type, authHeaders, venueID]);

  useEffect(() => { load(); }, [load]);

  // Sin `entity_id` no hay nada que cargar: si no se contempla aquí, el modal se queda con
  // la cabecera y ningún detalle, sin decir por qué.
  const hasDrillDown =
    Boolean(item.entity_id) &&
    (item.entity_type === "transaction" || item.entity_type === "product");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" {...overlayDismiss}>
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
       
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-bold text-slate-800">{item.summary || "Detalle"}</h2>
          <button
            onClick={onClose}
            className="text-2xl leading-none text-slate-400 hover:text-slate-700"
            aria-label="Cerrar"
          >×</button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          <Meta item={item} />

          {pairs.length > 0 && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg bg-slate-50 p-3 text-sm">
              {pairs.map((p) => (
                <React.Fragment key={p.label}>
                  <dt className="text-slate-400">{p.label}</dt>
                  <dd className="text-slate-700">{p.value}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}

          {loading && <p className="py-6 text-center text-sm text-slate-400">Cargando…</p>}

          {!loading && sale && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Qué se vendió</h3>
              <SaleDetail sale={sale} />
            </section>
          )}

          {!loading && history && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Últimos movimientos del producto
              </h3>
              <ProductHistory history={history} />
            </section>
          )}

          {!loading && hasDrillDown && drillDownUnavailable && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400">
              El detalle ampliado no está disponible para tu usuario.
            </p>
          )}

          {!loading && hasDrillDown && drillDownError && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              No se pudo cargar el detalle ampliado. Inténtalo de nuevo.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
